import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plane, Building2, BookOpen } from "lucide-react";
import DOMPurify from "dompurify";
import api from "../lib/api";
import ListenButton from "../components/ListenButton";
import TranslateAndListen from "../components/TranslateAndListen";
import DiscussionBoard from "../components/DiscussionBoard";
import AskCommunityCTA from "../components/AskCommunityCTA";
import { Spinner, vibrantTheme } from "../components/common";

function RelatedReading({ slug, entityType }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/articles-imported").then((r) => {
      const all = (r.data.items || []).filter((i) => i.slug !== slug);
      const same = all.filter((i) => i.entity_type === entityType);
      const rest = all.filter((i) => i.entity_type !== entityType);
      setItems([...same, ...rest].slice(0, 3));
    }).catch(() => setItems([]));
  }, [slug, entityType]);

  if (!items.length) return null;
  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 pb-16" data-testid="related-reading">
      <h2 className="font-heading font-900 text-2xl text-zinc-900 mb-5 flex items-center gap-2">
        <BookOpen className="w-6 h-6 text-primary" /> Related reading
      </h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {items.map((i) => (
          <Link key={i.slug} to={`/articles/${i.slug}`} data-testid={`related-article-${i.slug}`}
            className="group border border-zinc-300 bg-white overflow-hidden hover:border-primary hover:shadow-lg transition-all">
            {i.feature_image && <img src={i.feature_image} alt="" className="h-28 w-full object-cover" loading="lazy" />}
            <div className="p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{i.entity_type || "travel"}{i.entity_iata ? ` · ${i.entity_iata}` : ""}</div>
              <div className="font-heading font-900 text-sm text-zinc-900 mt-1 leading-snug group-hover:text-primary transition-colors line-clamp-2">{i.title}</div>
              <p className="text-xs text-zinc-600 mt-1.5 line-clamp-2">{i.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ArticleView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setA(null);
    api.get(`/articles-imported/${slug}`).then((r) => setA(r.data)).catch(() => {
      // fall back: this may be a news article slug — send it to the news reader
      api.get(`/articles/${slug}`).then(() => navigate(`/news/${slug}`, { replace: true })).catch(() => setA(false));
    });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (a === null) return <Spinner />;
  if (a === false) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-muted-foreground">Article not found.</div>;

  const theme = vibrantTheme(`article-${slug}`);
  const entityLink = a.entity_type === "airline" && a.entity_id ? `/airlines/${a.entity_id}`
    : a.entity_type === "airport" && a.entity_iata ? `/airports/${a.entity_iata}` : null;

  return (
    <div className="min-h-screen bg-background" data-testid="article-view-page">
      <div className="text-white" style={{ backgroundColor: theme.bg }}>
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
          <Link to="/articles" data-testid="back-to-articles" className="inline-flex items-center gap-1.5 text-sm font-700 text-white/85 hover:text-white mb-5">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <div className="text-xs font-mono uppercase tracking-widest text-white/75 mb-2 flex items-center gap-3 flex-wrap">
            <span>{a.category || "TravelerPulse Library"}</span>
            <TranslateAndListen className="text-white/90" />
          </div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl">{a.title}</h1>
          {a.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="article-tags">
              {a.tags.map((t) => (
                <span key={t} className="bg-white/15 px-2.5 py-1 text-xs font-mono lowercase tracking-wide">#{t}</span>
              ))}
            </div>
          )}
          {entityLink && (
            <Link to={entityLink} data-testid="article-entity-link"
              className="mt-4 inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors px-4 py-2 text-sm font-700">
              {a.entity_type === "airline" ? <Plane className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
              Open the {a.entity_type} profile ({a.entity_iata})
            </Link>
          )}
        </div>
      </div>
      {a.feature_image && (
        <div className="max-w-4xl mx-auto px-5 lg:px-8 -mt-6">
          <img src={a.feature_image} alt="" className="w-full max-h-[420px] object-cover border border-zinc-300" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-5 lg:px-8 pt-8 -mb-4">
        <ListenButton targetId="article-readable" />
      </div>
      <article id="article-readable" className="max-w-3xl mx-auto px-5 lg:px-8 py-10 article-body" data-testid="article-body"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.html, { FORBID_TAGS: ["style", "form"], ADD_ATTR: ["target"] }) }} />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 pb-14 text-sm text-muted-foreground">
        {a.source_url
          ? <>Originally published on <a href={a.source_url} target="_blank" rel="noreferrer" className="underline text-primary">SocialPulse</a>.</>
          : <>Researched and maintained by the TravelerPulse editorial team. Photos via Wikimedia Commons.</>}
      </div>
      <RelatedReading slug={slug} entityType={a.entity_type} />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 pb-6">
        <AskCommunityCTA entityName={a.title} targetId="discussion" />
      </div>
      <div id="discussion" className="max-w-3xl mx-auto px-5 lg:px-8 pb-16 scroll-mt-24" data-testid="article-discussion">
        <DiscussionBoard entityType="article" entityId={slug} entityName={a.title}
          placeholder={`Ask or share something about ${a.title}…`} />
      </div>
    </div>
  );
}
